import { useMemo } from 'react';
import { ChronoUnit, LocalDate } from '@js-joda/core';

import { FormContextValues } from '@dreryk/react-common';
import { DatePicker, dayValidator } from '@dreryk/react-components';

import { patientEntitlementsTranslationKeys, patientTranslationKeys } from '../../../translations';
import { PREGNANCY_PERIOD_IN_MONTHS } from '../../create/PatientEntitlementCreateForm.hooks';

export type PregnancyDateFieldsProps = Pick<FormContextValues, 'register' | 'watch'>;

export const PregnancyDateFields = (props: PregnancyDateFieldsProps): JSX.Element => {
   const startDate = props.watch('validityDateRange.start');
   const endDate = props.watch('validityDateRange.end');

   const maxEndDate = useMemo(
      () =>
         startDate
            ? (startDate instanceof LocalDate ? startDate : LocalDate.parse(startDate)).plus(
                 PREGNANCY_PERIOD_IN_MONTHS,
                 ChronoUnit.MONTHS
              )
            : undefined,
      [startDate]
   );

   const startValidators = useMemo(
      () => [
         dayValidator.dayAfterToday(patientEntitlementsTranslationKeys.startDateCantBeInFuture),
         dayValidator.dayAfterReferenceDay(endDate, patientEntitlementsTranslationKeys.startDateCantBeAfterEndDate),
      ],
      [endDate]
   );

   const endValidators = useMemo(
      () => [
         dayValidator.dayBeforeReferenceDay(LocalDate.now(), patientEntitlementsTranslationKeys.endDateCantBeInPast),
         dayValidator.dayAfterReferenceDay(
            maxEndDate,
            patientEntitlementsTranslationKeys.documentValidityRangeCantBeLongerThanNineMonths
         ),
      ],
      [maxEndDate]
   );

   return (
      <>
         <DatePicker
            controller={props.register('validityDateRange.start', { required: true })}
            label={patientEntitlementsTranslationKeys.validityFrom}
            placeholder={patientTranslationKeys.datePlaceholder}
            testId={'validityDateRangeStart'}
            withNavigationArrows
            dayValidators={startValidators}
         />
         <DatePicker
            controller={props.register('validityDateRange.end', { required: true })}
            label={patientEntitlementsTranslationKeys.validityTo}
            placeholder={patientTranslationKeys.datePlaceholder}
            testId={'validityDateRangeEnd'}
            withNavigationArrows
            dayValidators={endValidators}
         />
      </>
   );
};

PregnancyDateFields.fieldName = 'validityDateRange' as const;
